import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const AdminDoctorSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [regenerating, setRegenerating] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/admin/doctors/search?q=${encodeURIComponent(searchQuery)}`);
      setDoctors(response.data);
      if (response.data.length === 0) {
        setError('No doctors found matching your search');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Search failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEdit = (doctor) => {
    setSelectedDoctor(doctor);
    setEditForm({
      name: doctor.name,
      dob: doctor.dob,
      birth_city: doctor.birth_city,
      birth_time: doctor.birth_time,
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/admin/doctors/${selectedDoctor.id}`, editForm);
      // Refresh search results
      handleSearch();
      setEditMode(false);
      setSelectedDoctor(null);
    } catch (err) {
      const message = err.response?.data?.detail || 'Update failed. Please try again.';
      setError(message);
    }
  };

  const handleRegenerate = async (doctorId) => {
    if (!window.confirm('This will regenerate the poster. The old poster will be replaced. Continue?')) {
      return;
    }

    setRegenerating(true);
    try {
      await api.post(`/admin/doctors/${doctorId}/regenerate`);
      // Refresh search results
      handleSearch();
      alert('Poster regenerated successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Regeneration failed. Please try again.';
      setError(message);
    } finally {
      setRegenerating(false);
    }
  };

  const closeModal = () => {
    setEditMode(false);
    setSelectedDoctor(null);
    setEditForm({});
  };

  return (
    <div className="container admin-container" style={{ maxWidth: '1000px' }}>
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="page-title">Doctor Search</h1>
          <p className="page-subtitle">Search, edit, and regenerate doctor profiles</p>
        </div>
        <Link to="/admin" className="btn btn-secondary" style={{ width: 'auto', padding: '10px 20px' }}>
          ← Back
        </Link>
      </div>

      {/* Search Bar */}
      <div className="search-bar">
        <input
          type="text"
          className="form-input search-input"
          placeholder="Search by doctor name, MR name, or employee code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button
          onClick={handleSearch}
          className="btn btn-primary search-btn"
          disabled={loading}
        >
          {loading ? 'Searching...' : '🔍 Search'}
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}

      {/* Results Table */}
      {doctors.length > 0 && (
        <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Doctor Name</th>
                  <th>DOB</th>
                  <th>Birth City</th>
                  <th>MR Name</th>
                  <th>MR Code</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {doctors.map((doctor) => (
                  <tr key={doctor.id}>
                    <td style={{ fontWeight: '500' }}>Dr. {doctor.name}</td>
                    <td>{doctor.dob}</td>
                    <td>{doctor.birth_city}</td>
                    <td>{doctor.mr_name || '-'}</td>
                    <td>{doctor.mr_emp_code || '-'}</td>
                    <td>
                      <button
                        className="btn btn-secondary action-btn"
                        onClick={() => handleEdit(doctor)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-primary action-btn"
                        onClick={() => handleRegenerate(doctor.id)}
                        disabled={regenerating}
                      >
                        🔄 Regenerate
                      </button>
                      {doctor.preview_url && (
                        <a
                          href={doctor.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary action-btn"
                          style={{ display: 'inline-block', textDecoration: 'none' }}
                        >
                          👁️ View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editMode && selectedDoctor && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Doctor</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="form-group">
              <label className="form-label">Doctor Name</label>
              <div className="input-with-prefix">
                <span className="input-prefix">Dr.</span>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input
                type="date"
                className="form-input"
                value={editForm.dob}
                onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Birth City</label>
              <input
                type="text"
                className="form-input"
                value={editForm.birth_city}
                onChange={(e) => setEditForm({ ...editForm, birth_city: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Birth Time (HH:MM)</label>
              <input
                type="time"
                className="form-input"
                value={editForm.birth_time}
                onChange={(e) => setEditForm({ ...editForm, birth_time: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleSaveEdit}
                className="btn btn-primary"
              >
                💾 Save Changes
              </button>
              <button
                onClick={closeModal}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDoctorSearch;
